import ChartsView from 'components/Analytics/Chart/ChartsView'
import TableView from 'components/Analytics/Table/TableView'
import VersionSwitcher from 'components/Analytics/VersionSwitcher'
import dayjs from 'dayjs'
import { useAnalyticsData } from 'hooks/analytics/useAnalyticsData'
import { useAnalyticsPrefetch } from 'hooks/analytics/useAnalyticsPrefetch'
import { useAnalyticsTimeWindow } from 'hooks/analytics/useAnalyticsTimeWindow'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { TableKind } from 'types/analytics'
import { scrollToY } from 'utils/scroll'
import { isMobile } from 'utils/userAgent'

type VersionView = 'v2' | 'v3'

const DEFAULT_VERSION: VersionView = 'v3'
const DEFAULT_VIEW: TableKind = 'pools'

const isValidVersion = (value?: string): value is VersionView => value === 'v2' || value === 'v3'

const isValidView = (value?: string): value is TableKind => value === 'pools' || value === 'tokens'

const PageWrapper = styled.main`
  width: 100%;
  max-width: 1184px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 0 32px 48px;
  align-self: stretch;
  box-sizing: border-box;

  ${({ theme }) => theme.mediaWidth.upToLarge`
    padding: 0 28px 40px;
    gap: 28px;
  `}

  ${({ theme }) => theme.mediaWidth.upToMedium`
    padding: 0 24px 36px;
    gap: 24px;
  `}

  ${({ theme }) => theme.mediaWidth.upToSmall`
    padding: 0 16px 28px;
    gap: 20px;
  `}
`

const HeaderRow = styled.header`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  width: 100%;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    margin-top: 32px;
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 12px;
  `}
`

const HeaderSpacer = styled.div`
  min-width: 0;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    display: none;
  `}
`

const HeaderCenterGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(12px, 2vw, 32px);
  flex-wrap: wrap;
  text-align: center;
  min-width: 0;
`

const Title = styled.h1`
  margin: 0;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  font-size: clamp(1.75rem, 2.25vw + 1.1rem, 2.375rem);
  line-height: 1.1;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 1.75rem;
  `}

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    font-size: 1.5rem;
  `}
`

const AnalyticsStatusPill = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  background: ${({ theme }) => theme.backgroundSurface};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.85rem;
  line-height: 1.2;
  white-space: nowrap;
`

const HeaderStatusSlot = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 0;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    justify-content: center;
    width: 100%;
  `}
`

const ContentSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-width: 0;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    gap: 20px;
  `}
`

const ChartsSection = styled.section`
  width: 100%;
  min-width: 0;
`

const TableSection = styled.section`
  width: 100%;
  min-width: 0;
`

export default function Analytics() {
  const navigate = useNavigate()
  const params = useParams<{ version?: string; view?: string }>()

  const versionParam = params.version?.toLowerCase()
  const viewParam = params.view?.toLowerCase()

  const hasValidVersion = isValidVersion(versionParam)
  const versionView: VersionView = hasValidVersion ? versionParam : DEFAULT_VERSION

  const tableKind: TableKind = isValidView(viewParam) ? viewParam : DEFAULT_VIEW

  useEffect(() => {
    if (!versionParam || !hasValidVersion) {
      navigate('/explore/v3/pools', { replace: true })
      return
    }
    if (!viewParam) {
      navigate(`/explore/${versionView}/pools`, { replace: true })
      return
    }
    if (!isValidView(viewParam)) {
      navigate(`/explore/${versionView}/pools`, { replace: true })
    }
  }, [navigate, versionParam, viewParam, hasValidVersion, versionView])

  const analyticsTimeWindow = useAnalyticsTimeWindow()
  const timestamps = analyticsTimeWindow.timestamps
  const analyticsIndexedDateLabel = useMemo(() => {
    if (analyticsTimeWindow.indexedHeadTimestamp === undefined) return undefined

    const indexedAt = dayjs.unix(analyticsTimeWindow.indexedHeadTimestamp)
    const shouldShowIndexedStatus = indexedAt.isBefore(dayjs().subtract(1, 'minute'))
    if (!shouldShowIndexedStatus) return undefined

    return indexedAt.format('MMM D, YYYY')
  }, [analyticsTimeWindow.indexedHeadTimestamp])

  const { charts, pools, tokens } = useAnalyticsData({
    versionView,
    tableKind,
    timestamps,
    blocks: analyticsTimeWindow.blocks,
    latestIndexedBlockNumber: analyticsTimeWindow.latestIndexedBlockNumber,
  })

  const activePoolsData = versionView === 'v3' ? pools.v3.data : pools.v2.data
  const activeTokensData = versionView === 'v3' ? tokens.v3.data : tokens.v2.data
  const activeDatasetData = tableKind === 'pools' ? activePoolsData : activeTokensData

  const [chartsResetKey, setChartsResetKey] = useState(0)
  const chartsHydratedRef = useRef(false)

  const isCurrentReady = tableKind === 'pools' ? activePoolsData !== undefined : activeTokensData !== undefined

  useAnalyticsPrefetch({
    versionView,
    tableKind,
    currentReady: isCurrentReady,
    timestamps,
    blocks: analyticsTimeWindow.blocks,
    latestIndexedBlockNumber: analyticsTimeWindow.latestIndexedBlockNumber,
  })

  useEffect(() => {
    chartsHydratedRef.current = false
  }, [versionView, tableKind])

  useEffect(() => {
    if (!activeDatasetData || chartsHydratedRef.current) return
    chartsHydratedRef.current = true
    setChartsResetKey((value) => value + 1)
  }, [activeDatasetData])

  const pendingMobileScrollRef = useRef<number | null>(null)

  const rememberMobileScroll = () => {
    if (!isMobile || typeof window === 'undefined') return
    pendingMobileScrollRef.current = window.scrollY ?? window.pageYOffset ?? 0
  }

  useLayoutEffect(() => {
    if (!isMobile || typeof window === 'undefined') return
    const pending = pendingMobileScrollRef.current
    if (pending === null) return
    scrollToY(pending, 'auto')
    pendingMobileScrollRef.current = null
  }, [tableKind, versionView])

  const handleVersionChange = (version: VersionView) => {
    if (version === versionView) return
    rememberMobileScroll()
    navigate(`/explore/${version}/${tableKind}`)
  }

  const handleTableChange = (view: TableKind) => {
    if (view === tableKind) return
    rememberMobileScroll()
    navigate(`/explore/${versionView}/${view}`)
  }

  return (
    <PageWrapper>
      <HeaderRow>
        <HeaderSpacer />
        <HeaderCenterGroup>
          <Title>Kodiak</Title>
          <VersionSwitcher selected={versionView} onSelect={handleVersionChange} />
          <Title>Analytics</Title>
        </HeaderCenterGroup>
        <HeaderStatusSlot>
          {analyticsIndexedDateLabel && (
            <AnalyticsStatusPill>Analytics indexed up to {analyticsIndexedDateLabel}</AnalyticsStatusPill>
          )}
        </HeaderStatusSlot>
      </HeaderRow>

      <ContentSection>
        <ChartsSection>
          <ChartsView
            series={charts.series}
            isLoading={charts.isLoading}
            contextKey={`${versionView}-${chartsResetKey}`}
          />
        </ChartsSection>

        <TableSection>
          <TableView
            version={versionView}
            tableKind={tableKind}
            onTableKindChange={handleTableChange}
            pools={pools}
            tokens={tokens}
          />
        </TableSection>
      </ContentSection>
    </PageWrapper>
  )
}
